// ============================================================
// HEALTH CHECK-UP DATA — Structured Prototype Data
// ARCHITECTURE: Separate from UI. Replace with verified
// All Is Well package data when available from backend.
// STATUS: PROTOTYPE DATA — not connected to production
// ============================================================

export interface CheckupCategory {
  id: string;
  label: string;
  sublabel: string;
  iconType: string; // maps to Lucide icon name
  color: string;
  description: string;
}

export interface CheckupPackage {
  id: string;
  categoryId: string;
  name: string;
  tagline: string;
  includes: string[];
  suitableFor: string;
  note: string; // prototype disclaimer
}

export const checkupCategories: CheckupCategory[] = [
  {
    id: "general-wellness",
    label: "GENERAL WELLNESS",
    sublabel: "Comprehensive health overview",
    iconType: "Activity",
    color: "emerald",
    description: "A complete health assessment for overall wellbeing",
  },
  {
    id: "heart-health",
    label: "HEART HEALTH",
    sublabel: "Cardiac evaluation & screening",
    iconType: "Heart",
    color: "rose",
    description: "Focused cardiac screening and cardiovascular risk assessment",
  },
  {
    id: "diabetes",
    label: "DIABETES",
    sublabel: "Metabolic & glucose screening",
    iconType: "Droplets",
    color: "amber",
    description: "Diabetes screening, HbA1c, and metabolic health evaluation",
  },
  {
    id: "womens-health",
    label: "WOMEN'S HEALTH",
    sublabel: "Comprehensive women's wellness",
    iconType: "Flower2",
    color: "pink",
    description: "Tailored health screening for women at every stage of life",
  },
  {
    id: "senior-health",
    label: "SENIOR HEALTH",
    sublabel: "Comprehensive senior care",
    iconType: "Shield",
    color: "blue",
    description: "Thorough health evaluation designed for senior wellbeing",
  },
  {
    id: "executive-wellness",
    label: "EXECUTIVE WELLNESS",
    sublabel: "Premium executive health screen",
    iconType: "Briefcase",
    color: "gold",
    description: "Comprehensive executive health assessment with priority service",
  },
  {
    id: "preventive-health",
    label: "PREVENTIVE HEALTH",
    sublabel: "Early detection & prevention",
    iconType: "ShieldCheck",
    color: "teal",
    description: "Proactive health screening to detect concerns early",
  },
];

// PROTOTYPE DATA — Replace with verified All Is Well packages
export const checkupPackages: CheckupPackage[] = [
  {
    id: "pkg-general-wellness",
    categoryId: "general-wellness",
    name: "General Wellness Screen",
    tagline: "A comprehensive health overview",
    includes: [
      "Complete Blood Count (CBC)",
      "Blood Sugar (Fasting & PP)",
      "Lipid Profile",
      "Liver Function Test",
      "Kidney Function Test",
      "Thyroid Profile (TSH)",
      "Urine Routine",
      "Chest X-Ray",
      "ECG",
      "Physician Consultation",
    ],
    suitableFor: "Adults of all ages seeking a general health overview",
    note: "PROTOTYPE: Package details to be verified with All Is Well Hospital",
  },
  {
    id: "pkg-heart-health",
    categoryId: "heart-health",
    name: "Cardiac Wellness Screen",
    tagline: "Comprehensive cardiac evaluation",
    includes: [
      "ECG (Electrocardiogram)",
      "2D Echocardiography",
      "Lipid Profile",
      "Blood Sugar",
      "Blood Pressure Assessment",
      "TMT (Treadmill Test) — if indicated",
      "Cardiologist Consultation",
    ],
    suitableFor: "Adults with family history of heart disease or cardiac risk factors",
    note: "PROTOTYPE: Package details to be verified with All Is Well Hospital",
  },
  {
    id: "pkg-diabetes",
    categoryId: "diabetes",
    name: "Diabetes Care Screen",
    tagline: "Metabolic health evaluation",
    includes: [
      "HbA1c (Glycated Haemoglobin)",
      "Fasting Blood Sugar",
      "Post-Prandial Blood Sugar",
      "Insulin Resistance Panel",
      "Kidney Function Test",
      "Lipid Profile",
      "Urine Microalbumin",
      "Physician Consultation",
    ],
    suitableFor: "Individuals with diabetes, pre-diabetes, or metabolic concerns",
    note: "PROTOTYPE: Package details to be verified with All Is Well Hospital",
  },
  {
    id: "pkg-womens-health",
    categoryId: "womens-health",
    name: "Women's Wellness Screen",
    tagline: "Comprehensive women's health evaluation",
    includes: [
      "Complete Blood Count",
      "Thyroid Profile",
      "Hormonal Panel",
      "Pap Smear",
      "Mammography (age-appropriate)",
      "Bone Density (DEXA) — if indicated",
      "Gynaecologist Consultation",
    ],
    suitableFor: "Women aged 25 and above",
    note: "PROTOTYPE: Package details to be verified with All Is Well Hospital",
  },
  {
    id: "pkg-senior-health",
    categoryId: "senior-health",
    name: "Senior Care Screen",
    tagline: "Thorough evaluation for senior wellbeing",
    includes: [
      "Complete Blood Count",
      "Blood Sugar Panel",
      "Lipid Profile",
      "Kidney & Liver Function",
      "Thyroid Profile",
      "Bone Density Assessment",
      "ECG",
      "Chest X-Ray",
      "Eye & Hearing Screening",
      "Physician Consultation",
    ],
    suitableFor: "Adults aged 60 and above",
    note: "PROTOTYPE: Package details to be verified with All Is Well Hospital",
  },
  {
    id: "pkg-executive-wellness",
    categoryId: "executive-wellness",
    name: "Executive Health Screen",
    tagline: "Premium comprehensive health assessment",
    includes: [
      "Full Blood Panel",
      "Cardiac Evaluation (ECG + Echo)",
      "Pulmonary Function Test",
      "Abdominal Ultrasound",
      "Stress Test (TMT)",
      "Cancer Markers (PSA / CA-125)",
      "Nutritional Assessment",
      "Priority Physician Consultation",
      "Detailed Health Report",
    ],
    suitableFor: "Executives and professionals seeking comprehensive health evaluation",
    note: "PROTOTYPE: Package details to be verified with All Is Well Hospital",
  },
  {
    id: "pkg-preventive-health",
    categoryId: "preventive-health",
    name: "Preventive Health Screen",
    tagline: "Early detection for better outcomes",
    includes: [
      "Complete Blood Count",
      "Blood Sugar & HbA1c",
      "Lipid Profile",
      "Cancer Screening Markers",
      "Thyroid Profile",
      "Vitamin D & B12",
      "Urine Routine",
      "Physician Consultation",
    ],
    suitableFor: "Adults seeking proactive health monitoring",
    note: "PROTOTYPE: Package details to be verified with All Is Well Hospital",
  },
];

export function getPackageByCategory(categoryId: string): CheckupPackage | undefined {
  return checkupPackages.find((p) => p.categoryId === categoryId);
}
